import { formatMoney } from "@/utils/currency";

// The server's three-letter months (job_rules.day_name): en-GB's toLocaleDateString writes September as "Sept", and a
// job's name or a day must read the same whichever side wrote it (walk 15's C7).
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const isoParts = (iso: string): [number, number, number] => {
  const [year, month, day] = iso.split("-").map(Number);
  return [year, month, day];
};

/** "14 Nov 2026" from an ISO date, in the server's months, "·" when there is none. */
export const dateLabel = (iso: string | null | undefined): string => {
  if (!iso) return "·";
  const [year, month, day] = isoParts(iso);
  return `${day} ${SHORT_MONTHS[month - 1]} ${year}`;
};

/** "Sep 2026" from a month key "2026-09", in the server's months. */
export const shortMonthLabel = (month: string): string => {
  const [year, number] = month.split("-").map(Number);
  return `${SHORT_MONTHS[number - 1]} ${year}`;
};

/** "5 Sep" from an ISO date, in the server's months, with no year. */
export const dayMonthLabel = (iso: string): string => {
  const [, month, day] = isoParts(iso);
  return `${day} ${SHORT_MONTHS[month - 1]}`;
};

/**
 * A run of days as the server's job_rules.days_name writes it (J37): "11-14 Nov 2026" in one month, "28 Nov-3 Dec 2026"
 * across months, "28 Dec 2026-3 Jan 2027" across years, one day "11 Nov 2026".
 */
export function daysLabel(from: string, to: string | null): string {
  const [fromYear, fromMonth, fromDay] = isoParts(from);
  const day = (year: number, month: number, date: number): string => `${date} ${SHORT_MONTHS[month - 1]} ${year}`;
  if (to === null || to === from) return day(fromYear, fromMonth, fromDay);
  const [toYear, toMonth, toDay] = isoParts(to);
  if (fromYear !== toYear) return `${day(fromYear, fromMonth, fromDay)}-${day(toYear, toMonth, toDay)}`;
  if (fromMonth !== toMonth) return `${fromDay} ${SHORT_MONTHS[fromMonth - 1]}-${day(toYear, toMonth, toDay)}`;
  return `${fromDay}-${day(toYear, toMonth, toDay)}`;
}

/** A catalog item's label by id, "·" when it is unknown. */
export const labelOf = (items: { id: string; label: string }[] | undefined, id: string): string =>
  items?.find((item) => item.id === id)?.label ?? "·";

/** "October 2026" from a month key "2026-10". */
export const monthLabel = (month: string): string =>
  new Date(`${month}-01T00:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

/** A title with what it belongs to in front, when known: "Trattoria Nino · Social · October 2026". */
export const prefixed = (prefix: string | undefined, title: string): string => (prefix ? `${prefix} · ${title}` : title);

/** A percent as finance shows it: two decimals at most, a decimal comma. 7.5 → "7,5". */
export const pctText = (v: number): string => String(Math.round(v * 100) / 100).replace(".", ",");

/** "A", "A and B", "A, B and C". */
export const andList = (items: string[]): string => (items.length < 2 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);

/** A button that records money (DD-056): "Pay · 120,00 €", the verb alone while there is no amount. A typed 0 is no amount, as every missing list reads it. */
export const verbAmount = (verb: string, minor: number | null, currency: string): string => (!minor ? verb : `${verb} · ${formatMoney(minor, currency)}`);
